import React, { useState } from 'react';
import { Video, Brain, Heart, Shield, Sparkles, X } from 'lucide-react';
import { SessionType } from '../lib/tavus';

interface SessionOption {
  id: SessionType;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
}

interface AvatarSessionModalProps {
  onSelectSession: (sessionType: SessionType) => void;
  onClose: () => void;
}

const AvatarSessionModal: React.FC<AvatarSessionModalProps> = ({ onSelectSession, onClose }) => {
  const [selectedSession, setSelectedSession] = useState<SessionType | null>(null);

  const sessionOptions: SessionOption[] = [
    {
      id: 'check-in',
      title: 'Daily Check-in',
      description: 'A casual conversation to discuss how you\'re feeling today',
      icon: Heart,
      color: 'from-pink-400 to-pink-500'
    },
    {
      id: 'therapeutic',
      title: 'Therapeutic Session',
      description: 'A more structured session focused on specific mental health topics',
      icon: Brain,
      color: 'from-purple-400 to-purple-500'
    },
    {
      id: 'meditation',
      title: 'Guided Meditation',
      description: 'A calming session to help you relax and center yourself',
      icon: Sparkles,
      color: 'from-blue-400 to-blue-500'
    },
    {
      id: 'cbt',
      title: 'CBT Practice',
      description: 'Cognitive Behavioral Therapy exercises to challenge negative thoughts',
      icon: Brain,
      color: 'from-green-400 to-green-500'
    },
    {
      id: 'crisis',
      title: 'Crisis Support',
      description: 'Immediate support during difficult moments',
      icon: Shield,
      color: 'from-red-400 to-red-500'
    }
  ];

  const handleStartSession = () => {
    if (selectedSession) {
      console.log("Starting session with type:", selectedSession);
      onSelectSession(selectedSession);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-500 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Choose Your Session Type</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          <p className="text-slate-600 mb-6">
            Select the type of session you'd like to have with your Tavus avatar companion. Each session type provides different therapeutic approaches.
          </p>
          
          <div className="grid gap-4 mb-6">
            {sessionOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedSession(option.id)}
                className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
                  selectedSession === option.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${option.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <option.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-slate-900">{option.title}</h3>
                  <p className="text-sm text-slate-600">{option.description}</p>
                </div>
              </button>
            ))}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleStartSession}
              disabled={!selectedSession}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-colors disabled:opacity-50 flex-1 font-semibold"
            >
              Start Tavus Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarSessionModal;